// FLAGS: personal-data, consent, silent-failure
// The Customer.io CDP wrapper. The browser never holds the real write key: the SDK loads with a
// placeholder and every request is routed through OpenUp.Api's own `/api/v1/cio` relay, which
// substitutes the key server-side.
import { AnalyticsBrowser } from "@customerio/cdp-analytics-browser";
import { trackException } from "@src/appInsights/AppInsights";
import { CustomerIOInsightEvent } from "@src/appInsights/appInsights.types";

export interface CustomerIOInitializeConfig {
  cdpApiKey: string;
  region: string;
  cdnUrl: string;
}

export interface CustomerIOIdentifyParams {
  userId: string;
  traits: Record<string, unknown>;
}
const CIO_INTEGRATION_KEY = "Customer.io Data Pipelines";
let analytics: AnalyticsBrowser | null = null;

const toApiHost = (url: string): string =>
  `${url.replace(/^https?:\/\//, "").replace(/\/+$/, "")}/v1`;

// FLAGS: silent-failure
// Both failure paths only report to Application Insights and leave `analytics` null, after which every
// `identify` and `clearIdentify` returns early: tracking is off, sign-out resets nothing, and no UI
// surface says so.
const initialize = (config: CustomerIOInitializeConfig): void => {
  if (analytics) return;
  if (!config.cdpApiKey || !config.cdnUrl) return;

  try {
    analytics = AnalyticsBrowser.load(
      {
        writeKey: config.cdpApiKey,
        cdnURL: config.cdnUrl,
      },
      {
        integrations: {
          [CIO_INTEGRATION_KEY]: {
            apiHost: toApiHost(config.cdnUrl),
            protocol: "https",
          },
        },
      },
    );
    analytics.catch((err) => {
      trackException(CustomerIOInsightEvent.InitializeFailed, err);
    });
  } catch (err) {
    trackException(CustomerIOInsightEvent.InitializeFailed, err);
  }
};

// FLAGS: personal-data, consent
// Sends name, email, employer and the last-session/last-check-in dates to Customer.io with no consent
// check anywhere in the chain — not here, not in the caller, and `initialize` runs at import time. See
// hooks/MODULE.md for the caller and the trait list.
const identify = async ({
  userId,
  traits,
}: CustomerIOIdentifyParams): Promise<void> => {
  if (!analytics) return;

  try {
    await analytics.identify(userId, traits);
  } catch (err) {
    trackException(CustomerIOInsightEvent.IdentifyFailed, err);
  }
};

// FLAGS: silent-failure
// Logout's Customer.io reset, and `logout` does not await it. A rejection is reported to Application
// Insights only, so sign-out completes believing the identity was cleared when it may not have been.
const clearIdentify = async (): Promise<void> => {
  if (!analytics) return;

  try {
    await analytics.reset();
  } catch (err) {
    trackException(CustomerIOInsightEvent.ClearIdentifyFailed, err);
  }
};

export const customerioService = {
  initialize,
  identify,
  clearIdentify,
};

export default customerioService;
